/* Render-aware, area-weighted hero contrast. Only foreground ink is sampled.
   95% coverage is the project's acceptance rule, not a WCAG certification. */
export const MIN_CONTRAST = 4.5;
export const MIN_COVERAGE = .95;
const linear = Array.from({length:256}, (_,v) => {
  const s = v / 255;
  return s <= .04045 ? s / 12.92 : ((s + .055) / 1.055) ** 2.4;
});

export function scorePixels(pixels, ink) {
  let area = 0, black = 0, white = 0;
  for (let p = 0; p < ink.length; p += 4) {
    const weight = ink[p+3] / 255;
    if (!weight) continue;
    const luminance = .2126*linear[pixels[p]] + .7152*linear[pixels[p+1]] + .0722*linear[pixels[p+2]];
    area += weight;
    if ((luminance+.05)/.05 >= MIN_CONTRAST) black += weight;
    if (1.05/(luminance+.05) >= MIN_CONTRAST) white += weight;
  }
  return {area, black, white};
}

export function chooseTone(score, current = 'black') {
  const {area, black, white} = score;
  const tone = black === white ? current : black > white ? 'black' : 'white';
  return {tone, coverage:area ? score[tone]/area : 0, passes:area > 0 && score[tone]/area >= MIN_COVERAGE};
}

export class ToneStabilizer {
  constructor(tone = 'black') { this.tone=tone; this.pending=null; this.since=0; this.last=-Infinity; this.initial=true; this.phase='idle';this.transitionSwitched=false; }
  update(score, now, phase='idle') {
    if (phase!==this.phase) {this.phase=phase;this.pending=null;if(phase==='crossfade')this.transitionSwitched=false;}
    const best=chooseTone(score,this.tone);
    if (!score.area) return this.tone;
    if (phase==='loading' || (phase==='crossfade' && this.transitionSwitched)) return this.tone;
    if (best.tone === this.tone) { this.pending=null; this.initial=false; return this.tone; }
    if (this.pending !== best.tone) { this.pending=best.tone; this.since=now; }
    const advantage=(score[best.tone]-score[this.tone])/score.area;
    // During the picture dissolve, hand off once at the measured contrast
    // crossover. Outside it, ignore brief highlights/cuts instead of flickering.
    const fading=phase==='crossfade';
    const settle=fading?80:advantage >= .20 ? 350 : advantage >= .02 ? 650 : 1000;
    if (this.initial || (now-this.since >= settle && (fading || now-this.last >= 1000))) {
      this.tone=best.tone; this.last=now; this.pending=null; this.initial=false;
      if(fading)this.transitionSwitched=true;
    }
    return this.tone;
  }
}

const overlap = (a,b) => {
  const left=Math.max(a.left,b.left), top=Math.max(a.top,b.top);
  const right=Math.min(a.right,b.right), bottom=Math.min(a.bottom,b.bottom);
  return right>left && bottom>top ? {left,top,right,bottom,width:right-left,height:bottom-top} : null;
};
const box = r => ({left:Math.floor(r.left),top:Math.floor(r.top),right:Math.ceil(r.right),bottom:Math.ceil(r.bottom),width:Math.ceil(r.right)-Math.floor(r.left),height:Math.ceil(r.bottom)-Math.floor(r.top)});
const canvas = () => document.createElement('canvas');
const context = c => c.getContext('2d',{willReadFrequently:true});

export function startHeroTone(film, header) {
  const html=document.documentElement, state=new ToneStabilizer();
  const output=canvas(), layer=canvas(), mask=canvas();
  const out=context(output), lay=context(layer), ink=context(mask);
  const textCache=new WeakMap(), graphicCache=new WeakMap();
  let pendingGraphics=0, destroyed=false, timer, raf, lastSample=0;
  const style=e=>getComputedStyle(e);
  const viewport=()=>({left:0,top:0,right:innerWidth,bottom:innerHeight});
  const visible=e=>e.getClientRects().length && style(e).visibility==='visible' && style(e).display!=='none' && Number(style(e).opacity)>0;

  function clipFor(e) {
    let clip=overlap(viewport(),film.getBoundingClientRect());
    if (!clip || !visible(e)) return null;
    for (let p=e.parentElement;p && p!==document.body;p=p.parentElement) {
      const css=style(p);
      if (css.visibility!=='visible' || css.display==='none' || Number(css.opacity)===0) return null;
      if (/(hidden|clip|auto|scroll)/.test(css.overflowX+' '+css.overflowY)) {
        clip=overlap(clip,p.getBoundingClientRect()); if (!clip) return null;
      }
    }
    return clip;
  }

  function cropInk(data, rect, clip) {
    // Exclude horizontally clipped rail clones and offscreen content.
    for (let y=0;y<rect.height;y++) for (let x=0;x<rect.width;x++) {
      if (x+rect.left<clip.left || x+rect.left>=clip.right || y+rect.top<clip.top || y+rect.top>=clip.bottom) data[(y*rect.width+x)*4+3]=0;
    }
    return data;
  }

  function textInk(node, group, entries) {
    if (!node.textContent.trim()) return;
    const el=node.parentElement, clip=clipFor(el); if (!clip) return;
    // Visually hidden utility labels must not enter the score on mobile.
    const css=style(el); if (css.clipPath!=='none' || el.getBoundingClientRect().width<2) return;
    const range=document.createRange();range.selectNodeContents(node);
    const raw=range.getBoundingClientRect(); if (!overlap(raw,clip)) return;
    const r=box(raw), key=[node.textContent,css.font,css.textTransform,css.letterSpacing,r.width,r.height].join('|');
    let saved=textCache.get(node);
    if (!saved || saved.key!==key) {
      mask.width=r.width;mask.height=r.height;
      ink.font=css.font || `${css.fontWeight} ${css.fontSize} ${css.fontFamily}`;
      ink.fillStyle='#000';ink.textBaseline='alphabetic';
      for (let i=0;i<node.length;) {
        const char=String.fromCodePoint(node.textContent.codePointAt(i)), end=i+char.length;
        range.setStart(node,i);range.setEnd(node,end);i=end;
        if (!char.trim()) continue;
        const cr=range.getBoundingClientRect();
        const glyph=css.textTransform==='uppercase'?char.toUpperCase():css.textTransform==='lowercase'?char.toLowerCase():char;
        const m=ink.measureText(glyph), ascent=m.fontBoundingBoxAscent ?? parseFloat(css.fontSize)*.8, descent=m.fontBoundingBoxDescent ?? parseFloat(css.fontSize)*.2;
        ink.fillText(glyph,cr.left-r.left,cr.top-r.top+(cr.height-ascent-descent)/2+ascent);
      }
      saved={key,data:ink.getImageData(0,0,r.width,r.height).data};textCache.set(node,saved);
    }
    entries.push({group,rect:r,ink:cropInk(new Uint8ClampedArray(saved.data),r,clip)});
  }

  function addText(root, group, entries) {
    if (!root) return;
    const walker=document.createTreeWalker(root,NodeFilter.SHOW_TEXT);
    for (let node=walker.nextNode();node;node=walker.nextNode()) textInk(node,group,entries);
  }

  function svgImage(el) {
    let saved=graphicCache.get(el);if (saved) return saved;
    const clone=el.cloneNode(true), nodes=[el,...el.querySelectorAll('*')], copies=[clone,...clone.querySelectorAll('*')];
    nodes.forEach((node,i)=>{
      const css=style(node), copy=copies[i];
      for (const prop of ['fill','stroke']) copy.style.setProperty(prop,css[prop]==='none'?'none':'#000');
      for (const prop of ['stroke-width','stroke-linecap','stroke-linejoin','display']) copy.style.setProperty(prop,css.getPropertyValue(prop));
      copy.style.color='#000';
    });
    const r=el.getBoundingClientRect();clone.setAttribute('xmlns','http://www.w3.org/2000/svg');clone.setAttribute('width',r.width);clone.setAttribute('height',r.height);
    const image=new Image();saved={image,ready:false};graphicCache.set(el,saved);
    image.onload=()=>{saved.ready=true;schedule();};image.onerror=()=>{saved.failed=true;};
    image.src='data:image/svg+xml;charset=utf-8,'+encodeURIComponent(new XMLSerializer().serializeToString(clone));
    return saved;
  }

  function graphicInk(el, group, entries) {
    const clip=clipFor(el);if (!clip) return;
    const r=box(el.getBoundingClientRect());if (!overlap(r,clip)) return;
    const image=el.tagName.toLowerCase()==='img'?el:svgImage(el).image;
    if (!image.complete || !image.naturalWidth) { pendingGraphics++;return; }
    mask.width=r.width;mask.height=r.height;ink.drawImage(image,0,0,r.width,r.height);
    entries.push({group,rect:r,ink:cropInk(ink.getImageData(0,0,r.width,r.height).data,r,clip)});
  }

  function regions(menuOpen) {
    const entries=[];pendingGraphics=0;
    // With the opaque menu open, the user explicitly requests a fixed black
    // header and a video-derived hero tone. No independent header_tone exists.
    if (!menuOpen) {
      addText(header.querySelector('.gnb__nav'),'gnb',entries);
      header.querySelectorAll('.util__t,.count').forEach(e=>addText(e,'gnb',entries));
      header.querySelectorAll('.gnb__logo img').forEach(e=>graphicInk(e,'logo',entries));
      header.querySelectorAll('.gnb__utils svg').forEach(e=>graphicInk(e,'icons',entries));
    }
    film.querySelectorAll('.rail .nm').forEach(e=>addText(e,'index',entries));
    film.querySelectorAll('.rail .mt').forEach(e=>addText(e,'meta',entries));
    addText(film.querySelector('.counter'),'counter',entries);
    film.querySelectorAll('.ctrl svg').forEach(e=>graphicInk(e,'icons',entries));
    const track=film.querySelector('.track'), fill=track?.querySelector('span'), clip=track&&clipFor(track);
    if (clip) {
      const a=track.getBoundingClientRect(),b=fill.getBoundingClientRect();
      const r=box({left:a.left,right:a.right,top:Math.min(a.top,b.top),bottom:Math.max(a.bottom,b.bottom)});
      mask.width=r.width;mask.height=r.height;ink.fillStyle='#000';
      ink.fillRect(a.left-r.left,a.top-r.top,a.width,a.height);ink.fillRect(b.left-r.left,b.top-r.top,b.width,b.height);
      entries.push({group:'line',rect:r,ink:cropInk(ink.getImageData(0,0,r.width,r.height).data,r,clip)});
    }
    return entries;
  }

  function mediaDraw(ctx, media, rect) {
    const sw=media.videoWidth||media.naturalWidth, sh=media.videoHeight||media.naturalHeight;
    if (!sw || !sh) return false;
    const dest=media.getBoundingClientRect(),css=style(media);
    const scale=css.objectFit==='contain'?Math.min(dest.width/sw,dest.height/sh):Math.max(dest.width/sw,dest.height/sh);
    const [px='50%',py='50%']=css.objectPosition.split(' ');
    const offset=(v,space)=>v.endsWith('%')?parseFloat(v)*space/100:parseFloat(v)||0;
    const x=dest.left+offset(px,dest.width-sw*scale), y=dest.top+offset(py,dest.height-sh*scale);
    ctx.save();ctx.beginPath();ctx.rect(dest.left-rect.left,dest.top-rect.top,dest.width,dest.height);ctx.clip();
    ctx.drawImage(media,x-rect.left,y-rect.top,sw*scale,sh*scale);ctx.restore();return true;
  }

  function frameAt(r, slides, surfaces) {
    output.width=layer.width=r.width;output.height=layer.height=r.height;
    out.fillStyle=style(film).backgroundColor;out.fillRect(0,0,r.width,r.height);
    for (const slide of slides) {
      lay.clearRect(0,0,r.width,r.height);lay.globalAlpha=1;
      const picture=slide.querySelector('picture img'), video=slide.querySelector('video');
      let ready=false;
      if (picture?.complete && picture.naturalWidth) ready=mediaDraw(lay,picture,r);
      if (video?.readyState>=2) {
        const alpha=Number(style(video).opacity);
        if (alpha>0) {lay.globalAlpha=alpha;ready=mediaDraw(lay,video,r)||ready;}
      }
      if (!ready) throw new Error('media-pending');
      // Reproduce the current CSS overlay and actual crossfade opacities.
      lay.globalAlpha=1;lay.fillStyle=style(slide.querySelector('.veil')).backgroundColor;lay.fillRect(0,0,r.width,r.height);
      out.globalAlpha=Number(style(slide).opacity);out.drawImage(layer,0,0);out.globalAlpha=1;
    }
    // The opaque menu/header surfaces are part of the rendered background too.
    for (const el of surfaces) {
      const rect=el.getBoundingClientRect();if (!overlap(rect,r)) continue;
      out.globalAlpha=Number(style(el).opacity);out.fillStyle=style(el).backgroundColor;
      out.fillRect(rect.left-r.left,rect.top-r.top,rect.width,rect.height);out.globalAlpha=1;
    }
    // Composite once per sample, then read only the foreground regions. Drawing
    // a decoded high-resolution video again for every label stalls the UI.
  }

  function apply(tone) {if (html.dataset.uiTone!==tone) html.dataset.uiTone=tone;}
  function sample(now=performance.now()) {
    if (destroyed || document.hidden) return;
    if (header.classList.contains('is-solid')) {apply('black');return;}
    if (document.querySelector('.dr.is-on')) return;
    try {
      const slides=[...film.querySelectorAll('.slide')].filter(e=>visible(e)).sort((a,b)=>(parseInt(style(a).zIndex)||0)-(parseInt(style(b).zIndex)||0));
      const surfaces=[header,...header.querySelectorAll('.nav__panel')].filter(e=>visible(e));
      const menuOpen=!!header.querySelector('.nav__item:not(.is-shut):is(:hover,:focus-within,.is-open) > .nav__panel');
      const entries=regions(menuOpen), total={area:0,black:0,white:0}, groups={};
      film.dataset.uiMode=menuOpen?'menu-header-black':'unified';
      if (!slides.length || !entries.length) return;
      const bounds=box({left:Math.min(...entries.map(e=>e.rect.left)),top:Math.min(...entries.map(e=>e.rect.top)),right:Math.max(...entries.map(e=>e.rect.right)),bottom:Math.max(...entries.map(e=>e.rect.bottom))});
      frameAt(bounds,slides,surfaces);
      for (const entry of entries) {
        const r=entry.rect, pixels=out.getImageData(r.left-bounds.left,r.top-bounds.top,r.width,r.height).data;
        const score=scorePixels(pixels,entry.ink);
        const group=groups[entry.group] ||= {area:0,black:0,white:0};
        for (const key of ['area','black','white']) {total[key]+=score[key];group[key]+=score[key];}
      }
      if (!total.area) {film.dataset.uiContrast='pending';return;}
      const tone=state.update(total,now,film.dataset.heroPhase||'idle');apply(tone);
      const coverage=total.area?total[tone]/total.area:0;
      film.dataset.uiContrast=pendingGraphics?'pending':coverage>=MIN_COVERAGE?'pass':'needs-review';
      film.dataset.uiCoverage=coverage.toFixed(5);
      film.dataset.uiCoverageBlack=(total.black/total.area).toFixed(5);
      film.dataset.uiCoverageWhite=(total.white/total.area).toFixed(5);
      film.dataset.uiRegions=JSON.stringify(Object.fromEntries(Object.entries(groups).map(([k,v])=>[k,{area:Math.round(v.area),coverage:Number((v[tone]/v.area).toFixed(5))}])));
      film.dataset.uiSampleSource=slides.some(s=>s.querySelector('video.is-playing'))?'composited-video':'composited-poster';
      film.dataset.uiSampleMs=(performance.now()-now).toFixed(1);
    } catch (error) {
      // A CORS failure is not evidence of contrast. Preserve a stable fallback
      // and expose unverified status rather than reporting a fabricated pass.
      film.dataset.uiContrast=error.name==='SecurityError'?'unavailable':'pending';
      delete film.dataset.uiCoverage;
      if (!html.dataset.uiTone) apply(film.querySelector('.slide.is-on')?.dataset.uiToneFallback==='light'?'white':'black');
    }
  }
  function run() {raf=null;lastSample=performance.now();sample(lastSample);}
  function schedule() {if (!destroyed && !raf) raf=requestAnimationFrame(run);}
  function pulse() {if (performance.now()-lastSample>=(film.dataset.heroPhase==='crossfade'?45:190)) schedule();}
  const observer=new MutationObserver(schedule);
  film.querySelectorAll('.slide, .slide video').forEach(el=>observer.observe(el,{attributes:true,attributeFilter:['class','src']}));
  observer.observe(header,{attributes:true,attributeFilter:['class']});
  film.querySelectorAll('img,video').forEach(el=>['load','loadeddata','playing','error'].forEach(name=>el.addEventListener(name,schedule)));
  header.querySelectorAll('img').forEach(el=>el.addEventListener('load',schedule));
  ['pointerover','pointerout','focusin','focusout'].forEach(name=>header.addEventListener(name,schedule));
  window.addEventListener('resize',schedule);window.addEventListener('scroll',schedule,{passive:true});
  document.addEventListener('visibilitychange',schedule);document.addEventListener('gm:drawer',schedule);
  film.addEventListener('gm:hero-change',schedule);film.querySelector('.rail')?.addEventListener('scroll',schedule,{passive:true});
  document.fonts.ready.then(schedule);
  timer=setInterval(pulse,50);schedule();
  window.addEventListener('pagehide',()=>{destroyed=true;clearInterval(timer);cancelAnimationFrame(raf);raf=null;});
  window.addEventListener('pageshow',event=>{if(event.persisted){destroyed=false;timer=setInterval(pulse,50);schedule();}});
}

if (typeof document!=='undefined') {
  const film=document.getElementById('film'),header=document.getElementById('gnb');
  if (film && header) startHeroTone(film,header);
}
